import { useQueryClient } from '@tanstack/react-query';
import { useEventCallback } from '@mui/material';
import { isEqual } from 'lodash';

import {
  AppIssue,
  AppSupportRequest,
  IssuesResponse,
  SupportRequestsResponse,
  AppIssueBrief,
  AppSupportRequestBrief,
  IssueWithTags,
  SupportRequestWithTags,
  IssueId,
  SupportRequestId,
} from '@playq/services-beetle';
import { Environment } from '@playq/octopus-common';

import { useUpdateTags } from '/api'; // Adjust import
import { snackbarService } from '/common/snackbarService';
import { issueKeys, requestsKeys } from '/component/Support/BeetlesLookup/constants';

import { SupportContentType } from '../types';
import { useBeetleLookupContext } from '../context';

interface UseOptimisticUpdateTagsProps {
  beetleId: IssueId | SupportRequestId;
  type: SupportContentType;
  env: Environment;
}

export const useOptimisticUpdateContentTags = ({ beetleId, type, env }: UseOptimisticUpdateTagsProps) => {
  const { entity, setError, currentTags, retriveKeys } = useBeetleLookupContext();

  const queryClient = useQueryClient();

  const isIssuesType = type === SupportContentType.ISSUES;

  const listQueryKey = isIssuesType ? issueKeys : requestsKeys;
  const ONLY_CURRENT_FIRST_TAG = 0;
  const PayloadConstructor = isIssuesType ? IssueWithTags : SupportRequestWithTags;

  const { mutate, isPending: isSaving } = useUpdateTags(type, {
    onMutate: async (newItem) => {
      await queryClient.cancelQueries({ queryKey: retriveKeys });
      const previousItem = queryClient.getQueryData(retriveKeys);

      queryClient.setQueryData(retriveKeys, (old: (AppIssue | AppSupportRequest) | undefined) => {
        if (!old) return undefined;
        let newInstance: AppIssue | AppSupportRequest;

        if (old instanceof AppIssue) {
          newInstance = new AppIssue(old.serialize());
        } else {
          newInstance = new AppSupportRequest(old.serialize());
        }
        newInstance.tags = newItem.ids.flatMap((item) => item.tags);
        return newInstance;
      });

      return { previousItem };
    },
    onError: (_, _error, context) => {
      const ctx = context as { previousData?: AppIssue | AppSupportRequest };
      if (ctx.previousData) {
        queryClient.setQueryData(retriveKeys, ctx.previousData);
      }
      snackbarService.error('Failed to update tags.');
    },
    onSuccess: (_, variables) => {
      queryClient.setQueriesData({ queryKey: listQueryKey }, (oldResponse: unknown) => {
        if (oldResponse instanceof IssuesResponse) {
          const newList = oldResponse.issues.map((item) => {
            if (item.id.id === variables.ids[ONLY_CURRENT_FIRST_TAG].id.id) {
              const newItem = new AppIssueBrief(item.serialize());
              newItem.tags = variables.ids[ONLY_CURRENT_FIRST_TAG].tags;
              return newItem;
            }
            return item;
          });

          const newResponse = new IssuesResponse(oldResponse.serialize());
          newResponse.issues = newList;
          return newResponse;
        }

        if (oldResponse instanceof SupportRequestsResponse) {
          const newList = oldResponse.requests.map((item) => {
            if (item.id.id === variables.ids[ONLY_CURRENT_FIRST_TAG].id.id) {
              const newItem = new AppSupportRequestBrief(item.serialize());
              newItem.tags = variables.ids[ONLY_CURRENT_FIRST_TAG].tags;
              return newItem;
            }
            return item;
          });

          const newResponse = new SupportRequestsResponse(oldResponse.serialize());
          newResponse.requests = newList;
          return newResponse;
        }

        return oldResponse;
      });
      snackbarService.success('Tags have been updated');
    },
  });

  const hasChanges = !isEqual(entity.tags || [], currentTags);

  const handleSave = useEventCallback(() => {
    if (hasChanges) {
      const currentTagEntity = new PayloadConstructor();
      currentTagEntity.id = beetleId;
      currentTagEntity.tags = currentTags;
      mutate({ ids: [currentTagEntity], env });
      return;
    }
    setError('No changes made. Please add or remove at least one tag');
  });

  return { handleSave, hasChanges, isSaving };
};
